import React, { useCallback, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import {
  restrictToParentElement,
  restrictToVerticalAxis
} from '@dnd-kit/modifiers';
import { Td, Text, Flex, Tooltip, Box } from '@chakra-ui/react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';
import { faInfoCircle, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { Input, ButtonIcon, FormFeedback, useToast } from '@morressier/alembic';
import SortableRow from './SortableRow';
import { getValidationTypedMessage } from './common/errorMessages';
import FormFieldFigureSingleFileUpload from './FormFieldFigureSingleFileUpload';
import { FormFieldFile } from 'src/components/Submission/GenericFieldComponents/File';
import { formatMeta } from './FormFieldGraphicalAbstractImage';
import { IGraphicalAbstractImageValue } from 'src/pages/Organization/public/Journal/SubmissionUpload/components/GraphicalAbstractImage';
import { DeleteSubFigureModal } from '../Submission/GenericFieldComponents/Figures/DeleteSubFigureModal';

interface SubGraphicalAbstractImageProps {
  parentIndex: number;
  field: string;
  methods: UseFormReturn<IGraphicalAbstractImageValue>;
  validations: any;
  submissionId: string;
}

const SubGraphicalAbstractImage: React.FC<SubGraphicalAbstractImageProps> = ({
  parentIndex,
  field,
  methods,
  validations,
  submissionId
}) => {
  const {
    control,
    register,
    getValues,
    setValue,
    formState: { errors }
  } = methods;
  const toast = useToast();

  const [deleteSubFigureModal, setDeleteSubFigureModal] =
    useState<boolean>(false);
  const [selectSubIndex, setSelectSubIndex] = useState<number>(0);

  const {
    fields: subParts,
    move: moveSubFigure,
    remove: removeSubFigure
  } = useFieldArray({
    control,
    name: `${field}[${parentIndex}].subParts` as any
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  );

  const deleteSubFigure = useCallback(
    (subIndex: number) => {
      const subPartsPath = `${field}[${parentIndex}].subParts`;
      const currentSubParts = (getValues(subPartsPath as any) ?? []) as any[];
      const isDeletingLastSubFigure = currentSubParts.length <= 1;

      removeSubFigure(subIndex);
      if (isDeletingLastSubFigure) {
        const parentPath = `${field}[${parentIndex}]`;
        setValue(`${parentPath}.isComposite` as any, false, {
          shouldDirty: true,
          shouldValidate: true
        });
        setValue(`${parentPath}.file` as any, null, {
          shouldDirty: true,
          shouldValidate: true
        });
        setValue(`${parentPath}.metadata` as any, null, {
          shouldDirty: true,
          shouldValidate: true
        });
        setValue(`${parentPath}.subParts` as any, [], {
          shouldDirty: true,
          shouldValidate: true
        });
      }
      setDeleteSubFigureModal(false);
      toast({
        variant: 'success',
        title: `composite sub-figure deleted successfully.`
      });
    },
    [removeSubFigure, getValues, setValue, field, parentIndex, toast]
  );

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(ev: DragEndEvent) => {
          const { active, over } = ev;
          if (!over || active.id === over.id) return;

          const oldIndex = subParts.findIndex(s => s.id === active.id);
          const newIndex = subParts.findIndex(s => s.id === over.id);

          if (oldIndex !== -1 && newIndex !== -1) {
            moveSubFigure(oldIndex, newIndex);
          }
        }}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        <SortableContext
          items={subParts.map(s => s.id)}
          strategy={verticalListSortingStrategy}
        >
          {subParts.map((sub, subIndex) => {
            const subCaptionName = `${field}[${parentIndex}].subParts[${subIndex}].caption`;
            const subAltTextName = `${field}[${parentIndex}].subParts[${subIndex}].altText`;
            const subFileName = `${field}[${parentIndex}].subParts[${subIndex}].file`;
            const subMetaField = `${field}[${parentIndex}].subParts[${subIndex}].metadata`;
            const subMeta = getValues(subMetaField as any);
            const subCaptionError =
              errors?.fileSet?.GraphicalAbstractImage &&
              Array.isArray(errors.fileSet.GraphicalAbstractImage) &&
              errors.fileSet.GraphicalAbstractImage[parentIndex]?.subParts?.[subIndex]
                ?.caption;
            const subAltTextError =
              errors?.fileSet?.GraphicalAbstractImage &&
              Array.isArray(errors.fileSet.GraphicalAbstractImage) &&
              errors.fileSet.GraphicalAbstractImage[parentIndex]?.subParts?.[subIndex]
                ?.altText;

            return (
              <SortableRow key={sub.id} id={sub.id}>
                {({ attributes, listeners }: any) => (
                  <>
                    <Td ps={0}></Td>

                    <Td {...attributes} {...listeners} cursor="grab">
                      <FontAwesomeIcon icon={faGripDotsVertical} />
                    </Td>

                    <Td ps={0}>
                      <Text>{`Image ${parentIndex + 1}${String.fromCharCode(
                        97 + subIndex
                      )}`}</Text>
                    </Td>

                    <Td ps={0}>
                      <Flex direction="column" gap={{ base: 3, md: 4 }} width="100%">
                        <Flex direction="column">
                          <Input
                            placeholder="Enter sub-caption"
                            {...register(subCaptionName as any, {
                              setValueAs: (value: string) => value?.trim(),
                              onBlur: e => {
                                e.target.value = e.target.value.trim();
                              }
                            })}
                          />
                          {subCaptionError && (
                            <Flex mt={2}>
                              <FormFeedback variant="error">
                                {getValidationTypedMessage(
                                  'Caption',
                                  validations.kind,
                                  validations,
                                  subCaptionError
                                )}
                              </FormFeedback>
                            </Flex>
                          )}
                        </Flex>

                        <Flex direction="column">
                          <Input
                            placeholder="Enter sub alt-text"
                            {...register(subAltTextName as any, {
                              setValueAs: (value: string) => value?.trim(),
                              onBlur: e => {
                                e.target.value = e.target.value.trim();
                              }
                            })}
                          />
                          {subAltTextError && (
                            <Flex mt={2}>
                              <FormFeedback variant="error">
                                {getValidationTypedMessage(
                                  'Alt-Text',
                                  validations.kind,
                                  validations,
                                  subAltTextError
                                )}
                              </FormFeedback>
                            </Flex>
                          )}
                        </Flex>
                      </Flex>
                    </Td>

                    <Td></Td>

                    <Td ps={0}>
                      <Flex
                        alignItems={{ base: 'stretch', md: 'center' }}
                        direction={{ base: 'column', md: 'row' }}
                        w="100%"
                        gap={2}
                      >
                        <Box flex="1" width="100%">
                          <FormFieldFigureSingleFileUpload
                            submissionId={submissionId}
                            submissionContextId={submissionId}
                            methods={methods}
                            config={
                              {
                                name: 'file',
                                params: {
                                  validations: {
                                    required: true,
                                    maxSizeMB: validations.maxSizeMB,
                                    allowedFileFormats:
                                      validations.allowedFileFormats
                                  }
                                },
                                caseDataFields: { main: subFileName }
                              } as FormFieldFile
                            }
                          />
                        </Box>
                        <Flex
                          width={{ base: '100%', md: '88px' }}
                          justify={{ base: 'flex-start', md: 'flex-end' }}
                          gap={2}
                        >
                          <Box w="32px" display="flex" justifyContent="center">
                            {subMeta && (
                              <Tooltip
                                hasArrow
                                placement="top"
                                borderRadius={4}
                                maxW="300px"
                                p={3}
                                bg="gray.50"
                                color="black"
                                fontSize="sm"
                                whiteSpace="normal"
                                label={
                                  <div>
                                    <strong
                                      style={{
                                        fontSize: '13px',
                                        display: 'block',
                                        marginBottom: '6px'
                                      }}
                                    >
                                      File Info
                                    </strong>

                                    {Object.entries(formatMeta(subMeta)).map(
                                      ([key, value]) => (
                                        <div key={key}>
                                          <span style={{ fontWeight: 600 }}>
                                            {key}:{' '}
                                          </span>
                                          {value}
                                        </div>
                                      )
                                    )}
                                  </div>
                                }
                              >
                                <span>
                                  <ButtonIcon
                                    size="regular"
                                    ariaLabel="sub-figures-meta-info"
                                    icon={
                                      <FontAwesomeIcon icon={faInfoCircle} />
                                    }
                                    ml={1}
                                    tabIndex={-1}
                                  />
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                          <Box w="32px">
                            <ButtonIcon
                              size="regular"
                              ariaLabel="Delete the sub figure"
                              icon={<FontAwesomeIcon icon={faTrashAlt} />}
                              data-cy="delete-icon"
                              onClick={() => {
                                setDeleteSubFigureModal(true);
                                setSelectSubIndex(subIndex);
                              }}
                            />
                          </Box>
                        </Flex>
                      </Flex>
                    </Td>
                  </>
                )}
              </SortableRow>
            );
          })}
        </SortableContext>
      </DndContext>
      {deleteSubFigureModal && (
        <DeleteSubFigureModal
          onClose={() => setDeleteSubFigureModal(false)}
          isOpen={deleteSubFigureModal}
          onDelete={() => deleteSubFigure(selectSubIndex)}
          selectSubIndex={selectSubIndex}
        />
      )}
    </>
  );
};

export default SubGraphicalAbstractImage;
